import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    ToolbarButton,
    useGridApiContext,
    useGridSelector,
    gridDensitySelector,
    type GridDensity
} from '../lib/dataGrid';
import { clearPrefs } from './gridPreferences';
import { defineStoredString } from '../utils/storage';

const DENSITIES: GridDensity[] = ['compact', 'standard', 'comfortable'];

/**
 * One density preference for the whole app, kept for the user across sessions.
 * Renamed from `ast:gridDensity` when every key moved to one prefix; the stored
 * choice is carried over on the first read.
 */
const densityValue = defineStoredString<GridDensity>({
    key: 'grid_density',
    legacyKeys: ['ast:gridDensity'],
    allow: DENSITIES
});

/**
 * A settings menu (à la the MUI docs toolbar) to pick the grid density. The
 * choice is saved to localStorage and restored on mount, so it persists for the
 * user across grids and sessions. Renders inside a grid `<Toolbar>`.
 */
export default function GridSettingsMenu({ gridId }: { gridId?: string }) {
    const apiRef = useGridApiContext();
    const density = useGridSelector(apiRef, gridDensitySelector);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    // Restore the saved density once, when the grid mounts.
    const restored = useRef(false);
    useEffect(() => {
        if (restored.current) {
            return;
        }
        restored.current = true;
        const saved = densityValue.read();
        if (saved && saved !== density) {
            apiRef.current.setDensity(saved);
        }
    }, [apiRef, density]);

    function choose(value: GridDensity) {
        apiRef.current.setDensity(value);
        densityValue.write(value);
        setAnchorEl(null);
    }

    // Forget this grid's remembered layout and show the coded defaults again.
    // The defaults sit in the grid's `initialState`, which is only read while
    // the grid mounts — so this drops the storage and reloads, rather than
    // trying to reconstruct them.
    function resetLayout() {
        if (gridId) {
            clearPrefs(gridId);
        }
        setAnchorEl(null);
        window.location.reload();
    }

    return (
        <>
            <Tooltip title={<FormattedMessage id="grid.settings" />}>
                <ToolbarButton
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                    <SettingsIcon fontSize="small" />
                </ToolbarButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
            >
                <ListSubheader sx={{ lineHeight: 2 }}>
                    <FormattedMessage id="grid.density" />
                </ListSubheader>
                {DENSITIES.map((value) => (
                    <MenuItem
                        key={value}
                        selected={value === density}
                        onClick={() => choose(value)}
                    >
                        <ListItemIcon>
                            {value === density && (
                                <CheckIcon fontSize="small" />
                            )}
                        </ListItemIcon>
                        <ListItemText>
                            <FormattedMessage id={`grid.density.${value}`} />
                        </ListItemText>
                    </MenuItem>
                ))}
                {gridId && [
                    <Divider key="layout-divider" />,
                    <ListSubheader key="layout-header" sx={{ lineHeight: 2 }}>
                        <FormattedMessage id="grid.layout" />
                    </ListSubheader>,
                    // Reloads rather than re-deriving the defaults by hand:
                    // they live in each grid's `initialState`, which the grid
                    // only reads while mounting.
                    <MenuItem key="layout-reset" onClick={resetLayout}>
                        <ListItemIcon>
                            <RestartAltIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            <FormattedMessage id="grid.layout.reset" />
                        </ListItemText>
                    </MenuItem>
                ]}
            </Menu>
        </>
    );
}
